import { ApiResponse, BaseFetchParams, OmitFirstArg } from '../types';
import ky, { KyInstance, Options } from 'ky';

export type PointsBalance = {
  balance: number;
  frozen: boolean;
};

/**
 * Get user points balance
 * [need auth]
 * @param api - The KyInstance
 * @param options - Additional request options
 * @returns Promise resolving to points balance
 * @example
 * ```typescript
 * const result = await getPointsBalance(api);
 * ```
 */
export const getPointsBalance = async (api: KyInstance, options?: Options): Promise<ApiResponse<PointsBalance>> => {
  return await api.get<ApiResponse<PointsBalance>>('get-points-balance', options).json();
};

export type PointsHistoryParams = BaseFetchParams & {
  page: number;
  pageSize: number;
};

export type PointsHistoryEntry = {
  id: number;
  amount: number;
  source: number;
  refId: string;
  balanceAfter: number;
  createdAt: string;
  description: string;
};

export type PointsHistoryResult = {
  total: number;
  entries: PointsHistoryEntry[];
};

/**
 * Get user points history
 * [need auth]
 * @param api - The KyInstance
 * @param searchParams - Pagination params
 * @param options - Additional request options
 * @returns Promise resolving to points history
 * @example
 * ```typescript
 * const result = await getPointsHistory(api, { page: 1, pageSize: 10 });
 * ```
 */
export const getPointsHistory = async (
  api: KyInstance,
  searchParams: PointsHistoryParams,
  options?: Options
): Promise<ApiResponse<PointsHistoryResult>> => {
  return await api.get<ApiResponse<PointsHistoryResult>>('get-points-history', { ...options, searchParams }).json();
};

// ============================================================
// Points -> USDT Redemption (ADR-022)
// ============================================================

export type RedeemQuoteRequest = {
  /** Points amount to redeem (integer, must be > 0). */
  pointsAmount: number;
};

export type RedeemQuoteResult = {
  pointsAmount: number;
  /** USDT wei (string to preserve uint256 precision). */
  expectedUsdtOut: string;
  /** EIP-712 nonce, 0x-prefixed 64-hex string. */
  nonce: string;
  /** Unix timestamp seconds. */
  deadline: number;
  /** EIP-712 signature, 0x-prefixed 130-hex string. */
  signature: string;
  /** User wallet address (echoed; signature binds this). */
  userAddress: string;
  /** Deployed PointsRedemption contract address. */
  contractAddress: string;
  /** Chain id (BSC mainnet 56 / testnet 97). */
  chainId: number;
};

/**
 * Request a redemption quote: backend deducts Points, signs EIP-712, returns
 * everything needed to call PointsRedemption.redeem() on chain.
 * [need auth]
 */
export const redeemQuote = async (
  api: KyInstance,
  body: RedeemQuoteRequest,
  options?: Options
): Promise<ApiResponse<RedeemQuoteResult>> => {
  return await api.post<ApiResponse<RedeemQuoteResult>>('redeem-quote', { ...options, json: body }).json();
};

export type RedeemCancelRequest = {
  /** Nonce from a prior RedeemQuoteResult (the redemption to cancel). */
  nonce: string;
};

export type RedeemCancelResult = {
  success: boolean;
  pointsBalanceAfter: number;
};

/**
 * Cancel a pending redemption (user dismissed wallet popup before submitting tx).
 * Refunds the previously deducted points. Idempotent.
 * [need auth]
 */
export const redeemCancel = async (
  api: KyInstance,
  body: RedeemCancelRequest,
  options?: Options
): Promise<ApiResponse<RedeemCancelResult>> => {
  return await api.post<ApiResponse<RedeemCancelResult>>('redeem-cancel', { ...options, json: body }).json();
};

export type RedeemHistoryParams = BaseFetchParams & {
  page: number;
  pageSize: number;
};

export type RedeemHistoryEntry = {
  id: number;
  pointsAmount: number;
  /** USDT wei string; empty when status != 'confirmed'. */
  usdtOut: string;
  /** "pending" | "confirmed" | "cancelled" | "expired" */
  status: string;
  nonce: string;
  /** Tx hash, only populated when status == 'confirmed'. */
  txHash: string;
  /** ISO 8601. */
  createdAt: string;
};

export type RedeemHistoryResult = {
  total: number;
  entries: RedeemHistoryEntry[];
};

/**
 * List the user's redemption ledger entries (pending/confirmed/cancelled/expired).
 * [need auth]
 */
export const redeemHistory = async (
  api: KyInstance,
  searchParams: RedeemHistoryParams,
  options?: Options
): Promise<ApiResponse<RedeemHistoryResult>> => {
  return await api.get<ApiResponse<RedeemHistoryResult>>('redeem-history', { ...options, searchParams }).json();
};

export type PointsApi = {
  /**
   * Get user points balance
   * [need auth]
   */
  getPointsBalance: OmitFirstArg<typeof getPointsBalance>;
  /**
   * Get user points history
   * [need auth]
   */
  getPointsHistory: OmitFirstArg<typeof getPointsHistory>;
  /** Request a Points -> USDT redemption quote (ADR-022). */
  redeemQuote: OmitFirstArg<typeof redeemQuote>;
  /** Cancel a pending redemption (ADR-022). */
  redeemCancel: OmitFirstArg<typeof redeemCancel>;
  /** Paged history of user's redemptions (ADR-022). */
  redeemHistory: OmitFirstArg<typeof redeemHistory>;
};

export const createPointsApi = (initOptions: Options): PointsApi => {
  // get-points-balance / get-points-history are under /user; redeem-* are under /points.
  const userMergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/user` };
  const userApi = ky.create(userMergedOptions);
  const pointsMergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/points` };
  const pointsApi = ky.create(pointsMergedOptions);
  return {
    getPointsBalance: getPointsBalance.bind(null, userApi),
    getPointsHistory: getPointsHistory.bind(null, userApi),
    redeemQuote: redeemQuote.bind(null, pointsApi),
    redeemCancel: redeemCancel.bind(null, pointsApi),
    redeemHistory: redeemHistory.bind(null, pointsApi),
  };
};

export default createPointsApi;
